// Every word the masterminds' staff side shows (the Masterminds section, its member view and the
// accountability log's tools, the person page's card and lines, Today's term jobs, and the groups and
// registers on Recordings and dates), and the pure rules that turn the database's answers
// (docs/data-model.md 10.6.1 and 10.6.9) into those words. Pure: it imports only the date and name
// helpers and the Accelerator's limits (src/products/accelerator/limits.js), so
// tests/e2e/admin-mastermind.spec.js and tests/e2e/admin-masterminds.spec.js check every line on their own.
//
// The boards draw only 3 of these lines: AdminPerson.dc.html's "Group 2, month 3 of 12" and "Month 3
// of 12. 1 of 5 actions ticked this month. Attendance 2 of 2." (the board's guarantee words gone, the plan's
// question 2 on its default), and AdminToday's "The Property Accelerator. Nothing ticked since 30 Aug." (built in
// src/admin/today/words.js). The rest is undrawn, from docs/mastermind-admin-plan.md. Adam's writing rules
// hold: British English, numerals, no exclamation marks or dashes, straight quotes, sentence case,
// contractions, and a person named, never "he", "she" or "they". A mastermind is a group, never a room, and
// nothing here holds an amount, names The Property Accelerator, or says "evidence". "Guarantee" is said only on the
// guarantee's own 2 cards, Guarantee rules and Guarantee check, which only owner and admin see (Adam's answer of 20 Oct
// to the build plan's question 3; decision 56: members never see the word, so no member's page imports these).
import { staffFirstName } from '../../lib/names.js'
import { uk, shortDate, dayMonth } from '../../lib/format.js'
import { LIMITS, limitWords } from '../../products/accelerator/limits.js'

// 30 Aug, or 30 Aug 2025 when it isn't this year
export function day(t, nowMs = Date.now()) {
  if (!t) return ''
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(t) ? `${t}T12:00:00Z` : t
  return uk(iso).y === uk(nowMs).y ? dayMonth(iso) : `${dayMonth(iso)} ${uk(iso).y}`
}
// Sun 27 Sep, or Sun 27 Sep 2025
export function dayNamed(t, nowMs = Date.now()) {
  if (!t) return ''
  return uk(t).y === uk(nowMs).y ? shortDate(t) : `${shortDate(t)} ${uk(t).y}`
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const firstOf = person => staffFirstName(person) || 'They'

// ---- The term ----------------------------------------------------------------------------------------

// "month 3 of 12", the final month, or past the term (month 13 of a 12-month membership)
export function termWords(month, months) {
  if (!month) return ''
  if (!months) return `month ${month}`
  if (month > months) return `month ${month}, past the ${months}-month term`
  return `month ${month} of ${months}`
}
const cap = s => (s ? s[0].toUpperCase() + s.slice(1) : s)

// The access row's grey line on the person page (AdminPerson.dc.html: "Group 2, month 3 of 12"), from
// hub.mastermind_standing: the month counted from payments taken, so it matches what's been paid
export function standingDetail(st) {
  if (!st) return ''
  const term = termWords(st.term_month, st.term_months)
  if (st.cohort_name) return term ? `${st.cohort_name}, ${term}` : st.cohort_name
  return term ? cap(term) : '' // undrawn: a membership with no group
}

// The standing row for an access row, when hub.mastermind_standing picked that row (the open 1)
export const standingFor = (row, standing = []) => (row ? (standing || []).find(s => s.access_id === row.id) || null : null)
// The standing row for a product's progress line
export const standingOf = (productId, standing = []) => (standing || []).find(s => s.product_id === productId) || null

// ---- Attendance ----------------------------------------------------------------------------------------

// "Attendance 2 of 2": the sessions they came to, of the sessions judged that were theirs to come to
// (an excused absence and a session with no register line aren't counted, as the accountability log counts)
export function attendanceWords(st) {
  const judged = (st?.attended || 0) + (st?.missed || 0)
  if (!judged) return null
  return `Attendance ${st.attended || 0} of ${judged}`
}

// ---- The progress line (AdminPerson.dc.html's Progress card) -----------------------------------------

// With no session counted yet: every session so far excused (Adam, 7 Oct, the batch's question 11, in
// place of "No sessions held yet."), or none held (undrawn)
export function noCountedLine(st) {
  return st?.excused ? `${plural(st.excused, 'session')} excused, none counted yet.` : 'No sessions held yet.'
}

// "Month 3 of 12. 1 of 5 actions ticked this month. Attendance 2 of 2." Where they stand is the accountability
// log's (the Masterminds section and the person page's card for owner and admin), so the line never says it
// from the absences alone (the plan's question 2, on its default until Adam answers)
export function progressLine(st) {
  const parts = []
  if (st.term_month) parts.push(`${cap(termWords(st.term_month, st.term_months))}.`)
  if (st.cycle_id) {
    parts.push(st.actions_total
      ? `${st.actions_ticked || 0} of ${plural(st.actions_total, 'action')} ticked this month.`
      : 'No actions set this month.') // undrawn
  } else {
    parts.push('No actions set yet.') // undrawn
  }
  const att = attendanceWords(st)
  parts.push(att ? `${att}.` : noCountedLine(st)) // the second undrawn
  return parts.join(' ')
}
// The gold bar: how far through the 12 months (the board's 25% at month 3 of 12)
export function progressPct(st) {
  if (!st?.term_month || !st?.term_months) return 0
  return Math.min(100, Math.round(st.term_month / st.term_months * 100))
}

// ---- The panel ----------------------------------------------------------------------------------------

export const PANEL = {
  readOnly: 'Only an owner or admin can change these.',

  // the goal
  goal: 'Goal',
  goalAgreed: d => `Agreed on ${d}.`,
  goalChanged: d => `Changed by agreement on ${d}.`,
  goalDraft: first => `${first}'s draft. It isn't agreed yet.`,
  goalNone: 'No goal written yet.',
  goalLabel: 'The goal you agreed',
  goalPlaceholder: first => `Type the goal you agree with ${first}`,
  goalHint: first => `Lock it with the words you agree at ${first}'s first session. ${first} can't change it after that.`,
  // both numbers with a comma for thousands (the review of 11 Oct: it read "10308 of 12,000 characters")
  goalCount: n => `${limitWords(n)} of ${limitWords(LIMITS.goal)} characters`,
  goalTooLong: `A goal can be up to ${limitWords(LIMITS.goal)} characters.`,
  agreedOn: 'Agreed on',
  agreedOnHint: "The day you agreed it, never before they joined or in the future. It stays the first day it was agreed, through later changes.",
  agreedKept: d => `It was first agreed on ${d}, and that day stays.`,
  lock: 'Lock the goal',
  unlock: 'Unlock the goal',
  unlockHint: first => `Once it's unlocked, ${first} can change it again.`, // Adam, 7 Oct, the batch's question 11
  locked: first => `Goal locked. ${first} can't change it now.`,
  unlocked: first => `Goal unlocked. ${first} can change it again.`,
  reach: 'Mark as reached',
  reachedOn: 'Reached on',
  reachSave: 'Save',
  reachedLine: d => `Reached on ${d}.`,
  reachedDone: 'Saved. The goal is marked reached.',
  undo: 'Undo',
  undone: "Undone. It isn't marked reached now.",
  versions: n => `Agreed versions (${n})`,
  versionLine: (d, changed) => (changed ? `Changed by agreement on ${changed}` : `Agreed on ${d}`),

  // shared by this month's actions and the earlier months
  ticked: (n, total) => `${n} of ${total} ticked`,
  tickedOn: (first, d) => `Ticked by ${first} on ${d}`,
  // a tick the old site's copy didn't keep the day of
  tickedNoDay: first => `Ticked by ${first}`,
  notTicked: 'Not ticked yet',
  memberNote: first => `${first}'s note`,
  edit: 'Edit',
  save: 'Save',
  cancel: 'Cancel',
  saved: 'Saved.',
  setAt: 'Set at',
  noSession: 'Not at a session',
  sessionOption: (s, nowMs) => `${dayNamed(s.starts_at, nowMs)}, ${s.group || 'every group'}`,
  tooMany: `Give 1 to 20 actions, each up to ${limitWords(LIMITS.action)} characters.`,

  // wins
  wins: 'Wins',
  winsCount: (n, shared) => (n ? `${plural(n, 'win')}, ${shared} happy to share` : ''),
  noWins: first => `${first} hasn't logged a win yet.`,
  categories: { deal: 'Deal', finance: 'Finance', income: 'Income', marketing: 'Marketing', milestone: 'Milestone' },
  winLine: (category, d) => `${PANEL.categories[category] || category}, ${d}`, // the member board's "Marketing, 14 Sep"
  winLoggedBy: who => (who === 'staff' ? 'Logged by you' : who === 'import' ? 'Copied from the old site' : 'Logged by them'),
  shareTag: 'Happy to share',
  // a win the member logged: the yes to sharing it is theirs alone (private.guard_wins; undrawn)
  shareTagMember: first => `${first} is happy to share`,
  privateTag: 'Private',
  addWin: 'Add a win',
  winTitle: 'The win',
  winTitlePlaceholder: 'What happened, in a line',
  winCategory: 'Category',
  winDate: 'Date',
  winDetail: 'More about it',
  winDetailPlaceholder: 'Optional',
  winShare: first => `${first} is happy for you to share it`,
  // undrawn: a saved win, whoever logged it, shows the member's answer, which only they change (since
  // Adam's answer 6 of 7 Oct members change the share tick on every win of theirs)
  winShareMember: (first, yes) => (yes
    ? `${first} said yes to sharing it. Only ${first} can change that.`
    : `${first} hasn't said yes to sharing it, and only ${first} can.`),
  saveWin: 'Save the win', // the member board's button
  winAdded: 'Win added.',
  winSaved: 'Win saved.',
  deleteWin: 'Delete',
  deleteConfirm: title => `Delete "${title}"? It can't be undone.`,
  deleteYes: 'Delete it',
  keep: 'Keep it',
  winDeleted: 'Win deleted.',
  winTitleBad: `Give the win a title of up to ${limitWords(LIMITS.winTitle)} characters.`,
}

// The sessions a month can be set at: the group's, and every group's, that have started (newest
// first), each with its group's name
export function sessionChoices(sessions = [], { cohortId, cohorts = [], nowMs = Date.now() } = {}) {
  const names = new Map(cohorts.map(c => [c.id, c.name]))
  return sessions
    .filter(s => s.status !== 'cancelled' && s.starts_at && Date.parse(s.starts_at) <= nowMs + 12 * 3600 * 1000)
    .filter(s => !s.cohort_id || s.cohort_id === cohortId)
    .sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at))
    .map(s => ({ ...s, group: s.cohort_id ? names.get(s.cohort_id) || '' : null }))
}

// The register box's sessions (Adam, 18 Oct): the member's group's sessions and every group's, today's first (even before
// it starts), then the held ones, newest first, the last 12. today: the London day now, as YYYY-MM-DD; dayOf: a
// timestamp's London day.
export function registerChoices(sessions = [], { cohortId, today, dayOf, limit = 12 } = {}) {
  return sessions
    .filter(s => s.status !== 'cancelled' && s.starts_at && dayOf(s.starts_at) <= today)
    .filter(s => !s.cohort_id || s.cohort_id === cohortId)
    .sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at))
    .slice(0, limit)
}

// The month the next cycle takes, as private.start_next_cycle picks it with no month named: the later
// of the month after the last cycle's and the term month now
export function nextMonth(cycles = [], termMonth = 1) {
  const last = Math.max(0, ...cycles.map(c => c.month || 0))
  return Math.min(120, Math.max(last + 1, termMonth || 1))
}

// The actions typed into the box, 1 a line, blanks dropped
export const actionLines = text => String(text || '').split('\n').map(l => l.trim()).filter(Boolean)

// "Billed through": someone else on the row's billing, else the person's billing lead (owner, admin)
export function billedThrough({ personId, payerId, leadId, names = new Map() }) {
  const id = payerId && payerId !== personId ? payerId : leadId && leadId !== personId ? leadId : null
  if (!id) return null
  return { id, name: names.get(id) || 'another member' }
}

// ---- The Masterminds section (docs/mastermind-admin-plan.md 3): the members, the member view and the
// log's tools. Owner and admin, at 2-step login. ---------------------------------------------------------

const lowerFirst = s => (s ? s[0].toLowerCase() + s.slice(1) : s)
const list = items => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`)

export const MM = {
  nav: 'Masterminds',
  eyebrow: 'Masterminds',
  lede: "Each member's goal, monthly actions, sessions and wins, group by group.",
  pick: ({ name, groups, members, review }) => `${name}. ${plural(groups, 'group')}, ${plural(members, 'member')}. ${review} to review.`,
  open: 'Open',
  nothingHere: 'Nothing here',
  nothingLine: "There's no mastermind at this address.",
  noneYet: "No mastermind has moved into the hub yet. When one does, its members show here.",
  back: 'Back to Masterminds',

  // the group tabs
  groupsLabel: 'Groups',
  groupTab: (name, n) => `${name} (${n})`,
  noGroup: n => `No group (${n})`,
  everyone: n => `Everyone (${n})`,

  // after a session: the group's checklist for the 7 days after it (the critique's admin 7), each line ticked once it's
  // done
  met: (group, day, left, total) => `${group} met on ${day}. ${left} of ${total} still to review.`,
  metDone: (group, day) => `${group} met on ${day}.`,
  checklist: group => `After ${group}'s session`,
  checkRegister: (marked, total) => (marked >= total ? `Register: all ${total} marked` : `Register: ${marked} of ${total} marked`),
  takeRegister: 'Take the register',
  checkRecording: on => (on ? 'Recording: on' : 'Recording: not on yet'),
  addRecording: 'Add the recording',
  checkNext: (day, time, zoom) => `Next session: ${day}, ${time}, ${zoom ? 'Zoom link set' : 'no Zoom link yet'}`,
  checkNoNext: 'Next session: none set yet',
  setIt: 'Set it',
  checkReviews: (left, total) => (left ? `Reviews: ${left} of ${total} to do` : `Reviews: all ${total} done`),
  checkDone: 'Done',
  checkToDo: 'To do',
  reviewStart: name => `Review, starting with ${name}`,
  registerFor: day => `Register for ${day}`,
  whatsappYes: 'WhatsApp link added',
  whatsappNo: 'No WhatsApp link yet',

  // the tools
  find: 'Find a member',
  findPlaceholder: 'Name',
  show: 'Show',
  all: 'Everyone',
  needs: n => `Needs you (${n})`,
  // anyone A little behind, Behind or Well behind on their accountability (levels.js)
  off: n => `Behind (${n})`,
  // anyone overdue, suspended or paused (the critique's admin 5)
  money: n => `Payment or pause (${n})`,
  // the legend over the members, from levels.js's bands: "On track: every session live and every action done. A little
  // behind: 1 or 2 slips. Behind: 3 to 5. Well behind: 6 or more, or no session live."
  legend: bands => bands.map((b, i) => {
    const word = MM.level[b.key] || b.key
    if (b.to === 0) return `${word}: every session live and every action done.`
    if (b.to === Infinity) return `${word}: ${b.from} or more, or no session live.`
    const span = b.to === b.from + 1 ? `${b.from} or ${b.to}` : `${b.from} to ${b.to}`
    return `${word}: ${span}${i === 1 ? ' slips' : ''}.`
  }).join(' '),
  slipIs: 'A slip is a session not attended live or an action not done.',
  // a search across every group: each match's group beside the name
  inGroup: group => group || 'No group',
  // a row Behind gets the nudge: the log's tab with Send open, under Before you send it
  sendLog: 'Send the log',
  sendLogTo: name => `Send the log to ${name}`,
  // under the level, once the summary says when the log was last sent (the build plan's migration 80)
  logSent: d => `Log sent ${d}`,
  logNotSent: 'Log not sent',

  // the table and the phone cards
  cols: { member: 'Member', month: 'Month', thisMonth: 'This month', sessions: 'Sessions', actions: 'Actions', stand: 'Where they stand', needs: 'Needs you' },
  monthOf: (m, n) => (m > n ? `${m}, after ${n}` : `${m} of ${n}`),
  // "0 of 3 ticked, last 30 Aug": the last tick on any of their actions, where it's known
  ticked: (a, b, last) => `${a} of ${b} ticked${last ? `, last ${last}` : ''}`,
  noActionsSet: 'No actions set',
  // attended live, of the sessions counted, as the level counts them (the critique's admin 2)
  sessionsOf: (a, b) => `Live ${a} of ${b}`,
  actionsDone: (a, b) => `Done ${a} of ${b}`,
  noneYet2: 'None yet',
  // every other need on a row, after the first: "Also: 1 register mark, goal to agree"
  also: items => `Also: ${items.join(', ')}`,
  alsoWords: {
    review: m => `month ${m} to review`,
    register: n => plural(n, 'register mark'),
    goal: 'goal to agree',
    actions: m => `month ${m}'s actions to set`,
  },
  noneReviewed: 'None reviewed',
  stand: { on_track: 'On track', off_track: 'Off track', getting_started: 'Getting started' },
  measure: { sessions: 'sessions', actions: 'actions', goal: 'goal', membership: 'membership' },
  offWhat: off => `Off track: ${list(off.map(k => MM.measure[k] || k))}`,
  // where they stand on their accountability, as the member's log says it (src/products/mastermind/levels.js, Adam,
  // 18 Oct, 15:45): "Behind: 2 sessions missed, 3 actions not done", "Well behind: none of 3 sessions attended live"
  level: { on_track: 'On track', a_little_behind: 'A little behind', behind: 'Behind', well_behind: 'Well behind', getting_started: 'Getting started' },
  levelLine: lv => {
    const word = MM.level[lv.key] || MM.level.getting_started
    const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`
    const facts = [
      lv.never ? `none of ${plural(lv.held, 'session', 'sessions')} attended live` : lv.sessionsMissed ? `${plural(lv.sessionsMissed, 'session', 'sessions')} missed` : null,
      lv.notDone ? `${plural(lv.notDone, 'action', 'actions')} not done` : null,
    ].filter(Boolean)
    return facts.length && lv.key !== 'on_track' && lv.key !== 'getting_started' ? `${word}: ${facts.join(', ')}` : word
  },
  // a level set by hand (Adam's answer to question 2, 20 Oct; migration 80), for owner and admin only: the member's
  // pages show the level alone, never the note or who set it; a copy of their data, if they ask for one, holds the level
  // and the note (the review of 22 Oct). who: 'you', a first name, or another admin
  levelSetHead: ({ who, day, level, auto }) => `Set by ${who} on ${day}: ${level}. Automatic: ${auto}.`,
  levelNote: (who, note) => (who === 'you' ? `Your note: ${note}` : `${who.charAt(0).toUpperCase()}${who.slice(1)}'s note: ${note}`),
  levelSetCell: (level, who) => `${level}, set by ${who}`,
  anotherAdmin: 'another admin',
  setLevel: 'Set the level by hand',
  changeLevel: 'Change the level',
  backToAuto: 'Back to automatic',
  levelField: first => `${first}'s level`,
  levelAutoLine: auto => `Automatic, from the log: ${auto}.`,
  levelNoteField: 'Why (only owners and admins see this)',
  levelNoteHint: first => `${first} sees only the level on their pages, never this note. A copy of their data, if they ask for one, holds both.`,
  levelSave: 'Save the level',
  levelSaved: (first, level) => `Saved. ${first} sees ${level} now.`,
  backToAutoTitle: first => `Put ${first} back to automatic?`,
  backToAutoBody: (first, auto) => `${first}'s level becomes ${auto}, as the log counts it, and the note goes.`,
  backToAutoYes: 'Back to automatic',
  backToAutoNo: 'Keep it',
  levelCleared: (first, auto) => `Back to automatic. ${first} sees ${auto} now.`,
  levelBeforeSend: (first, level, auto) => `${first}'s level is set by hand: the log says ${level}. Automatic: ${auto}.`,
  // the open month's session (the critique's admin 8; migration 80), in the review's own words ("Month 3 starts at the
  // session on", setNextAt; the review of 22 Oct)
  monthSessionLine: (n, day) => (day ? `Month ${n} starts at the session on ${day}.` : `Month ${n} doesn't start at a session.`),
  monthSessionChange: 'Change',
  monthSessionField: n => `Month ${n} starts at the session on`,
  monthSessionHint: n => (n > 1
    ? `Month ${n}'s review falls due once a later session is held. Month ${n - 1} then counts as reviewed at this session.`
    : `Month ${n}'s review falls due once a later session is held.`),
  monthSessionSave: 'Save',
  monthSessionDone: (n, day) => (day ? `Month ${n} starts at the session on ${day} now.` : `Month ${n} doesn't start at a session now.`),
  // what a change of session did to the guarantee check, when it moved it (the month before's span moves with it, and a
  // month wholly inside a pause doesn't count; the review of 22 Oct): in the done line and History
  guaranteeMoved: (before, after) => `The guarantee check went from ${before} to ${after}.`,
  guaranteeCountsMoved: 'What counts in the guarantee check changed.',
  access: { paused: 'Paused', suspended: 'Suspended', overdue: 'Overdue', ended: 'Ended', revoked: 'Revoked' },
  // the status line under a member's name when their access isn't active (the critique's admin 5), with the person
  // page's own buttons. Suspended is the 7-day unpaid rule; Paused is a pause Adam or Stripe set.
  statusOverdue: (failed, suspendOn) => `Overdue: ${failed ? `payment failed ${failed}` : 'a payment failed'}.${suspendOn ? ` Suspended from ${suspendOn} unless paid.` : ''}`,
  statusSuspended: since => (since ? `Suspended since ${since}, unpaid` : 'Suspended, unpaid'),
  statusPaused: (by, until) => (by === 'stripe'
    ? (until ? `Paused in Stripe until ${until}` : 'Paused in Stripe')
    : (until ? `Paused by you until ${until}` : 'Paused by you')),
  statusLabel: first => `${first}'s membership`,
  needsReview: m => `Review month ${m}`,
  needsRegister: 'Register missing',
  needsGoal: 'Agree the goal',
  needsActions: m => `Set month ${m}'s actions`,
  // Actions only once the summary has the log's own counts (memberRow)
  phoneLine: ({ month, thisMonth, sessions, actions }) => `Month ${month}. ${thisMonth}. Sessions: ${sessions}.${actions ? ` Actions: ${actions}.` : ''}`,
  phoneNeeds: what => `Needs you: ${lowerFirst(what)}`,
  emptyGroup: group => `Nobody's in ${group} yet. Change a member's group from their view here, or from their page in People.`,
  noMatch: 'Nobody matches that. Clear the search and the filter to see everyone.',
  leftH: "Members who've left",
  left: n => `Members who've left (${n})`,
  leftShow: "Show who's left",
  leftHide: "Hide who's left",
  leftNone: "Nobody's left yet.",
  lastStatus: status => ({ ended: 'Ended', revoked: 'Revoked' }[status] || status),

  // Guarantee rules (owner and admin only: the build plan's question 3, Adam, 20 Oct), the numbers every member's
  // guarantee check uses; where they stand on their accountability is the legend's, apart from these
  rulesH: 'Guarantee rules',
  rulesLines: ({ limit, catchUp, pct, days, months }) => [
    `Up to ${plural(limit, 'session')} missed in the first ${months} months. A session caught up within ${catchUp} days isn't missed.`,
    `At least ${pct}% of actions done.`,
    `Goal agreed within ${days} days of joining.`,
    'Membership kept up to date.',
  ],
  change: 'Change',
  fields: {
    limit: months => `Sessions that can be missed in the first ${months} months`,
    pct: 'Share of actions done, at least',
    days: 'Days to agree the goal',
    catchUp: 'Days to catch up on a missed session',
  },
  bounds: { limit: 'From 0 to 12.', pct: 'From 50 to 100%.', days: 'From 1 to 60.', catchUp: 'From 1 to 60.' },
  saveRules: 'Save the numbers',
  // what the numbers change, said before they're saved (rulesEffect in section.js): the guarantee check only, never
  // where anyone stands on their accountability
  rulesSaved: () => "Saved. Every member's guarantee check uses these numbers now.",
  rulesEffectH: 'Before you save',
  rulesEffect: names => (names.length
    ? `These numbers change ${names.length === 1 ? `${names[0]}'s guarantee check` : `the guarantee check for ${plural(names.length, 'member')}: ${list(names)}`}.`
    : "These numbers don't change any member's guarantee check as it stands now."),
  rulesEffectCatchUp: 'A change to the days to catch up may change more checks, as each catch-up is judged again.',
  rulesEffectAll: 'They apply to every member, whenever they joined.',
  rulesOwner: 'Only an owner can change these.',
  rulesBad: 'Each number is a whole number within its bounds.',

  // ---- the member view
  // the membership's month, counted as the term counts it (payments taken plus paused days), apart from the month of
  // actions on This month (the critique's admin 2)
  headLine: ({ group, month, months, joined }) => [group, month ? `Membership month ${MM.monthOf(month, months)}` : null, joined ? `Joined ${joined}` : null].filter(Boolean).join('. ') + '.',
  personPage: 'Person page',
  more: 'More',
  moreFor: first => `More for ${first}`,
  changeGroup: 'Change group',
  groupField: 'Group',
  noGroupOption: 'No group',
  moveGroup: 'Move to this group',
  moved: (first, group) => (group ? `Done. ${first} is in ${group} now.` : `Done. ${first} has no group now.`),
  prev: name => `Previous: ${name}`,
  next: name => `Next: ${name}`,
  position: (i, n, group) => (group ? `${i} of ${n} in ${group}` : `${i} of ${n}`),
  // the stepper when Adam came through "Review, starting with" (the critique's admin 16)
  positionReview: (i, n) => `${i} of ${n} to review`,
  backTo2: group => (group ? `Done: back to ${group}` : 'Done: back to the list'),
  // the Everyone tab by its name, for the stepper's position and its way back when Adam stepped through it
  everyoneName: 'Everyone',
  // the crumbs on a phone: "< Group 2"
  backToGroup: group => group || 'Masterminds',
  tabs: { month: 'This month', log: 'Accountability log' },
  // the strip under the tabs: attended live and actions done, as the log counts them, each marked as the level is, then
  // the goal and the membership as dated facts (the critique's admin 2)
  strip: {
    sessions: (live, counted) => (counted ? `Live ${live} of ${counted}` : 'No sessions counted yet'),
    actions: (done, due) => (due ? `Actions done ${done} of ${due}` : 'No actions reviewed yet'),
    goal: { agreed: d => `Goal agreed ${d}`, reached: d => `Goal reached ${d}`, none: 'Goal not agreed yet' },
    membership: { upToDate: 'Membership up to date', suspended: spans => `Suspended ${spans}`, overdue: 'Payment overdue', paused: d => `Paused since ${d}` },
  },
  // a span of days, short: "8 to 12 Sep", "28 Oct to 3 Nov", "since 3 Nov"
  span: (from, to) => (to ? `${from} to ${to}` : `since ${from}`),
  loading: 'Loading.',

  // This month, as it is between sessions: the month of actions, apart from the membership's month in the head
  monthTitle: m => `Month ${m}'s actions`,
  setAtAside: (setDay, atSession, ticked, total, last) => `${atSession ? `Set at the session on ${setDay}` : `Set on ${setDay}`}. ${MM.ticked(ticked, total, last)}.`,
  // with migration 80 the card's first line says the session it starts at, with Change, so its aside keeps the ticks
  // alone and the session is said once (the review of 22 Oct)
  ticksAside: (ticked, total, last) => `${MM.ticked(ticked, total, last)}.`,
  note: (first, note) => `${first}'s note: ${note}`,
  carriedFrom: m => `Carried from month ${m}`,
  carriedTimes: (first, hops) => `Set in month ${first}, carried over ${hops === 1 ? '1 time' : `${hops} times`}`,
  editWords: 'Edit words',
  setAside: 'Set aside',
  putBack: 'Put it back',
  setAsideTag: reason => `Set aside: ${reason}`,
  addTo: m => `Add to month ${m}`,
  addLabel: 'Actions, 1 a line',
  addPlaceholder: 'Each line becomes 1 action',
  addButton: n => (n > 1 ? `Add ${n} actions` : n === 1 ? 'Add 1 action' : 'Add actions'),
  addHint: first => `${first} sees them straight away and ticks each one as it's done.`,
  added: (n, m, started) => (started ? `Month ${m} started with ${plural(n, 'action')}.` : `${plural(n, 'action')} added to month ${m}.`),
  reviewNow: m => `Review month ${m} now`,
  noMonth: 'No month open yet.',
  startMonth: m => `Start month ${m}`,
  noActions: 'No actions in this month yet.',

  // This month, at its review
  reviewLede: first => `How did each action go? ${first}'s ticks are filled in for you.`,
  // the review's opening line (the critique's admin 14): "Sam ticked 4 of 4. All set to Done. Change any that aren't right."
  reviewOpening: (first, ticked, total) => (!total
    ? `${first} has no actions to review this month.`
    : ticked === total
      ? `${first} ticked ${ticked} of ${total}. All set to Done. Change any that aren't right.`
      : ticked
        ? `${first} ticked ${ticked} of ${total}. Those are set to Done and the rest to Not done. Change any that aren't right.`
        : `${first} ticked none of ${total}. All set to Not done. Change any that aren't right.`),
  outcomeLabel: body => `How it went: ${body}`,
  outcomes: { done: 'Done', part_done: 'Part done', not_done: 'Not done' },
  carryTo: m => `Carry to month ${m}`,
  newActions: m => `Month ${m}'s new actions, 1 a line`,
  setNextAt: m => `Month ${m} starts at the session on`,
  closeAndGo: (m, name) => `Close month ${m} and go to ${name}`,
  close: m => `Close month ${m}`,
  // under the black button, when there's someone to go on to
  closeStay: first => `Close and stay on ${first}`,
  closeLast: m => `Close month ${m} without starting another`,
  // the 10-second hold before the month closes (the critique's admin 8): Undo while it holds, sent when it ends or as
  // Adam steps on, closes another member's month or leaves the page
  closing: m => `Closing month ${m} in 10 seconds.`,
  closingFor: (first, m) => `Closing ${first}'s month ${m} in 10 seconds.`,
  undo: 'Undo',
  undone: m => `Undone. Month ${m} is still open, as it was.`,
  closeFailed: (first, m, why) => `${first}'s month ${m} didn't close. ${why}`,
  // named, as it shows on the next member's page when Adam goes straight on
  closed: ({ first, month, done, part, not, carried, next, total }) => {
    const counts = [done ? `${done} done` : null, part ? `${part} part done` : null, not ? `${not} not done` : null].filter(Boolean).join(', ')
    const head = `${first ? `${first}'s month` : 'Month'} ${month} closed${counts ? `: ${counts}` : ''}.`
    const carry = carried && next ? ` ${carried} carried to month ${next}.` : ''
    const started = next ? ` Month ${next} started with ${plural(total, 'action')}.` : ''
    return head + carry + started
  },
  cancelReview: 'Not now',

  // Set aside, and Edit words
  asideTitle: 'Set this action aside',
  asideField: 'Why is it set aside?',
  asidePlaceholder: 'For example, the purchase fell through, agreed at the session',
  asideNote: first => `It's left out of ${first}'s count and shows on the log with your reason.`,
  asideYes: 'Set it aside',
  asideNo: 'Keep it',
  asideDone: 'Set aside.',
  asideNeeded: "Say why it's set aside.",
  outcomesNeeded: 'Say how each action went first.',
  reasonNeeded: "Say why it's changing.",
  putBackDone: first => `Put back. It's in ${first}'s list again.`,
  editLabel: 'The action',

  // Earlier months
  earlierH: 'Earlier months',
  earlierCount: n => (n ? `${plural(n, 'month')} reviewed` : 'None reviewed yet'),
  earlierLine: ({ month, setDay, atSession, reviewDay, reviewedAtSession }) => `Month ${month}. ${atSession ? `Set at the session on ${setDay}` : `Set on ${setDay}`}.${reviewDay ? ` Reviewed ${reviewedAtSession ? 'on' : 'on'} ${reviewDay}.` : ''}`,
  noEarlier: 'No earlier months yet.',
  marks: { done: 'Done', part_done: 'Part done', not_done: 'Not done', set_aside: 'Set aside' },
  carriedTo: m => `Carried to month ${m}`,
  workedOut: 'Worked out',
  workedOutTitle: 'Worked out from the ticks, as no outcome was set at the time',
  correct: 'Correct',
  correctTitle: 'Correct how it went',
  correctField: 'How it went',
  reasonField: "Why it's changing",
  reasonHint: 'Up to 300 characters. It shows in the history of changes.',
  correctSave: 'Save the correction',
  correctNote: 'The log shows it was updated, and on which day.',
  corrected: 'Saved. The log shows it was updated today.',
  copiedTag: 'Copied from the old site',

  // Needs you
  needsH: 'Needs you',
  needReview: (m, day) => `Review month ${m}. ${day}'s session has been held.`,
  needRegister: day => `No register mark for ${day}.`,
  needGoal: first => `Goal not agreed yet. Agree it at ${first}'s next session.`,
  needActions: m => `No actions set for month ${m}.`,
  needCarried: body => `Carried 3 times: ${body}`,
  needCopied: months => `${months.length === 1 ? `Month ${months[0]}` : `Months ${months[0]} to ${months[months.length - 1]}`} came from the old members site, so how each action went is worked out from the ticks there.`,

  // Sessions
  sessionsH: 'Sessions',
  showAll: n => `Show all ${n}`,
  showFewer: 'Show the last 6',
  sessionWhen: (day, group) => `${day}, ${group || 'every group'}`,
  markWords: { present: 'Present', absent: 'Absent', excused: 'Excused' },
  marksLabel: day => `Mark for ${day}`,
  noRegister: 'No register taken',
  openRegister: 'Open the register',
  pausedLine: 'Paused, not counted',
  outsideTerm: 'Outside the first 12 months, not counted',
  caughtUpOn: 'Caught up on',
  caughtSave: 'Save',
  caughtClear: 'Clear',
  hint: (watched, update) => [watched ? `Recording watched ${watched}.` : null, update ? `Last action update ${update}.` : null].filter(Boolean).join(' '),
  caughtInTime: day => `Saved. ${day} counts as caught up.`,
  caughtLate: n => `Saved. It came ${n} days after, so it still counts as missed.`,
  caughtCleared: 'Cleared.',
  caughtBadDate: "The day is from the session's day to today.",
  agreedBadDate: "The day it was agreed can't be before they joined or in the future.",
  reachedBadDate: "The day it was reached is from the day it was agreed to today.",
  caughtLine: (day, inTime) => (inTime ? `Caught up on ${day}.` : `Caught up on ${day}, too late to count.`),
  markSaved: (day, mark) => `Saved. ${day}: ${mark.toLowerCase()}.`,

  // The register box at the top of a member's page (Adam, 18 Oct): pick the session, mark them, step to the next
  attendH: 'Register',
  attendLede: first => `Pick the session, then mark ${first}. Next and Previous keep the session you picked.`,
  attendSession: 'Session',
  attendWhen: (day, time) => `${day}, ${time}`,
  attendToday: (day, time) => `Today, ${day}, ${time}`,
  // 1 set of words wherever a mark is given: Present, Absent, Excused, with Excused's hint (the critique's admin 3)
  attendMarks: { present: 'Present', absent: 'Absent', excused: 'Excused' },
  excusedHint: 'Excused: agreed in advance.',
  // once they're marked, the box is 1 line: "Sun 27 Sep: absent." with Change
  attendFolded: (day, mark) => `${day}: ${mark.toLowerCase()}.`,
  attendChange: 'Change',
  attendLabel: first => `Mark ${first} for this session`,
  attendNow: (first, mark) => (mark ? `${first} is marked ${mark.toLowerCase()}.` : `${first} isn't marked yet.`),
  attendNotYet: time => `This session starts at ${time}. You can mark it once it's started.`,
  attendNone: first => `${first}'s group has no sessions yet.`,
  registerNote: note => `Register note: ${note}`,
  noSessionsYet: first => `No sessions of ${first}'s group have been held yet.`,

  // Guarantee check (owner and admin only: the build plan's question 3, Adam, 20 Oct), opening with its verdict
  termH: () => 'Guarantee check',
  termOpen: 'Guarantee check',
  termVerdictMeets: 'Meets the guarantee so far.',
  termVerdictShort: reasons => `Doesn't meet the guarantee so far: ${reasons.join('; ')}.`,
  termVerdictNone: 'Nothing to judge yet.',
  termWhy: {
    sessions: (missed, limit) => `${plural(missed, 'session')} missed, up to ${limit} allowed`,
    actions: (pct, need) => `actions ${pct}% done, ${need}% needed`,
    goal_late: days => `goal agreed after the first ${days} days`,
    goal_not: days => `goal not agreed in the first ${days} days`,
    membership: spans => `suspended ${spans}`,
  },
  termProof: 'See the proof',
  termGoal: {
    agreed: (days, day) => `Goal agreed within ${days} days of joining: yes, on ${day}.`,
    reached: (days, day) => `Goal agreed within ${days} days of joining: yes, and reached on ${day}.`,
    // reached, but agreed late, or before its days were judged
    reachedLate: (days, agreed, day) => `Goal agreed within ${days} days of joining: no, it was agreed on ${agreed}. Reached on ${day}.`,
    reachedUnjudged: (days, agreed, day) => `Goal agreed within ${days} days of joining: not judged, as those days ended before the hub kept the record. Agreed on ${agreed}, reached on ${day}.`,
    agreed_unjudged: (days, day) => `Goal agreed within ${days} days of joining: not judged, as those days ended before the hub kept the record. It was agreed on ${day}.`,
    agreed_late: (days, day) => `Goal agreed within ${days} days of joining: no, it was agreed on ${day}.`,
    not_agreed: days => `Goal agreed within ${days} days of joining: no.`,
    not_yet: (days, day) => `Goal agreed within ${days} days of joining: not yet, and there's until ${day}.`,
    to_do: () => "Goal: not judged, as its days ended before the hub kept the record.",
  },
  termSessions: (missed, limit) => `Sessions: ${missed} missed. Up to ${limit} allowed.`,
  termActions: (pct, need) => (pct == null ? `Actions: none reviewed yet. ${need}% needed.` : `Actions: ${pct}% done. ${need}% needed.`),
  termMemberNone: since => (since ? `Membership: no suspension recorded since ${since}.` : 'Membership: no suspension recorded.'),
  termMemberSusp: spans => `Membership: suspended ${spans}.`,
  termEnd: (end, paused, claims) => `The first 12 months end on ${end}${paused ? `, with ${plural(paused, 'paused day')} added` : ''}. Extension claims close on ${claims}.`,
  termRunning: months => `The first ${months} months end after month ${months}'s payment.`,

  // History of changes
  historyH: 'History of changes',
  showMore: 'Show more',
  historyNone: 'No changes recorded yet.',
  // a change made outside anyone's login: the old site's copy (folded into 1 line, the critique's admin 13), or after
  // it a fix to the record (or test data on staging)
  the: 'A fix to the record',
  oldSite: 'The old site',
  copiedFold: n => `From the old site's copy (${plural(n, 'change')})`,
  ev: {
    goal_drafted: who => `${who} drafted the goal.`,
    goal_changed: who => `${who} changed the goal's words.`,
    goal_locked: (who, changed) => (changed ? `${who} locked the goal, changed by agreement on ${changed}.` : `${who} locked the goal.`),
    goal_unlocked: who => `${who} unlocked the goal.`,
    goal_reached: (who, on) => (on ? `${who} marked the goal reached on ${on}.` : `${who} took the goal off reached.`),
    action_added: (who, body) => `${who} added "${body}".`,
    carried: (who, body) => `${who} carried "${body}" into the next month.`,
    ticked: (who, body) => `${who} ticked "${body}".`,
    unticked: (who, body) => `${who} unticked "${body}".`,
    noted: (who, body) => `${who} wrote a note on "${body}".`,
    edited: (who, before, after) => `${who} changed "${before}" to "${after}".`,
    outcome_set: (who, body, outcome) => `${who} marked "${body}" ${outcome}.`,
    outcome_corrected: (who, body, outcome, reason) => `${who} corrected "${body}" to ${outcome}${reason ? `: "${reason}"` : ''}.`,
    verdict_changed: (who, body) => `${who} changed the verdict on "${body}".`,
    set_aside: (who, body, reason) => `${who} set "${body}" aside${reason ? `: ${reason}` : ''}.`,
    put_back: (who, body) => `${who} put "${body}" back.`,
    log_sent: who => `${who} sent the accountability log.`,
    log_saved: who => `${who} saved the accountability log as a PDF.`,
    attendance_changed: (day, before, after) => (before && after
      ? `${day}'s mark changed from ${before} to ${after}.`
      : after ? `${day} marked ${after}.` : `${day}'s mark taken away.`),
    catch_up_recorded: (who, day, on) => (on ? `${who} recorded ${day} as caught up on ${on}.` : `${who} cleared the catch-up for ${day}.`),
    month_closed: (who, month, done, part, not) => `${who} closed month ${month}: ${[done ? `${done} done` : null, part ? `${part} part done` : null, not ? `${not} not done` : null].filter(Boolean).join(', ') || 'nothing to review'}.`,
    accountability_log_shared: (who, how) => (how === 'sent' ? `${who} sent the accountability log.` : `${who} saved the accountability log as a PDF.`),
    // the level set by hand and the open month's session (migration 80)
    accountability_level_set: (who, before, after) => (before
      ? `${who} changed the level from ${before} to ${after}.`
      : `${who} set the level to ${after} by hand.`),
    // the note changed, the level kept (the review of 22 Oct)
    accountability_level_note: (who, level) => `${who} changed the note on the level (${level}).`,
    accountability_level_cleared: who => `${who} put the level back to automatic.`,
    month_session_changed: (who, month, day, none) => (none
      ? `${who} took month ${month} off its session.`
      : day ? `${who} set month ${month} to start at the session on ${day}.` : `${who} changed the session month ${month} starts at.`),
  },
  historyLine: (day, line) => `${day}. ${line}`,

  // The log's tab: Adam's tools above the document, never printed
  beforeH: 'Before you send it',
  registerGaps: days => `${plural(days.length, 'session')} ${days.length === 1 ? 'has' : 'have'} no register mark: ${days.join(', ')}.`,
  reviewDue: (m, day) => `Month ${m} is due for review: ${day}'s session has been held.`,
  goalNot: first => `${first}'s goal isn't agreed yet.`,
  allRecorded: "Everything's recorded.",
  print: 'Print or save as PDF',
  printHint: 'In the print box, choose Save as PDF. Turn off Headers and footers if your browser shows them.',
  printFailed: "That didn't save. Try again.",
  send: first => `Send to ${first}`,
  cantSend: (first, product) => `${first} can't open ${product}'s pages now, so save it as a PDF and attach it to your own email.`,
  sendTitle: first => `Email ${first} a link to their accountability log?`,
  sendLede: first => `${first} gets an email with a link to the log in the hub, and logs in with a code to see it. A copy of the log as it stands now is kept here.`,
  lineField: 'A line from you (optional)',
  linePlaceholder: "For example, here's where you're at before the next session.",
  lineTooLong: 'A line can be up to 500 characters.',
  sendYes: 'Send the link',
  sendNo: 'Not now',
  sent: (first, email) => `Sent to ${first} at ${email}. A copy of the log as sent is kept.`,
  lastSent: (day, time) => `Last sent on ${day} at ${time}.`,
  copiesKept: items => `Copies kept: ${items.join('. ')}.`,
  copiesKeptLabel: 'Copies kept:',
  copyItem: (kind, day, time) => (kind === 'log_sent' ? `sent ${day}, ${time}` : `saved as a PDF ${day}, ${time}`),
  copyNotThere: "That copy isn't there. Here's the log as it stands.",
  backTo: first => `Back to ${first}`,

  // The person page's card
  cardAside: 'Goal, actions, sessions and wins',
  cardLine: ({ group, month, months, stand }) => [group, month ? `Month ${MM.monthOf(month, months)}` : null, stand].filter(Boolean).join('. ') + '.',
  // the actions only once the summary holds the log's own counts (memberRow)
  cardMeasures: ({ sessions, actions, goal, membership }) => [sessions, actions, goal, membership].filter(Boolean).map(t => `${t}.`).join(' '),
  cardNeeds: what => `Needs you: ${lowerFirst(what)}.`,
  // the card's way in says where it goes (the critique's admin 13)
  goalAndActions: 'Open in Masterminds',
  logButton: 'Accountability log',
  cardGoal: { agreed: 'Goal agreed', agreed_unjudged: 'Goal agreed, not judged', agreed_late: 'Goal agreed late', reached: 'Goal reached', not_yet: 'Goal not agreed yet', not_agreed: 'Goal not agreed', to_do: 'Goal to agree' },
}

// ---- Refusals: the staff definers raise the code as the message (data-model 10.6.1) -------------------

// The line for a refusal from the mastermind's staff writes. ctx: { first, product, group, lastMonth }
export function refusalWords(err, ctx = {}) {
  const code = err?.code
  const msg = String(err?.message || '')
  const first = ctx.first || 'This person'
  if (/View as member is read only/i.test(msg) || code === 'viewing_as') return "You're viewing as a member, so nothing here can be changed."
  switch (msg) {
    case 'not_allowed': return 'Only an owner or admin can do that.'
    case 'no_person': return "We can't find that person any more. Refresh the page."
    case 'not_mastermind': return 'Something went wrong. Try again.'
    case 'no_access': return `${first} doesn't have ${ctx.product || 'this product'}, so there's nothing to set.`
    case 'goal_empty': return `${first} hasn't drafted a goal yet. Type it in, then lock it.`
    case 'goal_too_long': return PANEL.goalTooLong
    case 'no_goal': return ctx.reach ? `${first}'s goal isn't agreed yet, so it can't be marked reached.` : `${first} hasn't drafted a goal yet, so there's nothing to unlock.`
    case 'bad_actions': return PANEL.tooMany
    case 'bad_session': return `Pick 1 of ${ctx.group || 'the group'}'s sessions, or 1 for every group.`
    case 'bad_month': return `The month has to come after ${firstOf({ first_name: ctx.first })}'s last cycle, month ${ctx.lastMonth ?? 1}.`
    case 'bad_verdict': return 'Pick Verified, In progress or Behind.'
    case 'no_action': return 'That action has gone. Refresh the page.'
    // the accountability log's writes (migration 20261013210000)
    case 'no_open_month': return ctx.monthSession ? "There's no month open now. Refresh the page." : "There's no month open to close. Refresh the page."
    case 'outcomes_missing': return 'Say how each action went first.'
    case 'month_changed': return 'This month changed since you opened it. Refresh the page to see it as it is now.'
    case 'bad_outcome': return 'Pick Done, Part done or Not done for each action.'
    case 'carry_done': return 'Only an action part done or not done carries over.'
    case 'month_open': return 'An action in the open month is reviewed when the month closes.'
    case 'month_closed': return "That month is closed, so it can't be changed here. Correct how it went from Earlier months."
    case 'set_aside': return 'Put it back first.'
    case 'reason_needed': return ctx.aside ? "Say why it's set aside." : "Say why it's changing, in up to 300 characters."
    case 'carried_on': return ctx.carriedTo ? `This action was carried to month ${ctx.carriedTo}. Mark it there.` : 'This action was carried on. Mark it in the month it went to.'
    case 'bad_date': return ctx.dateLine || "That day can't be used. Pick 1 from the right range."
    case 'not_absent': return 'Only an absence can be caught up on.'
    case 'bad_rules': return MM.rulesBad
    case 'no_log': return `${first} has no accountability log on ${ctx.product || 'this mastermind'}.`
    case 'rules_in_masterminds': return "A mastermind's numbers are changed in Masterminds, under Guarantee rules."
    // only the member can say yes to sharing a win of theirs, whoever logged it (private.guard_wins since
    // migration 20261007110000; undrawn); the panel never sends the share tick for a saved win, so this
    // shows only if something else does
    case 'share_member_only': return `Only ${first} can say yes to sharing this win.`
    // the level set by hand and the open month's session (migration 80)
    case 'bad_level': return 'Pick On track, A little behind, Behind or Well behind.'
    case 'session_too_early': return ctx.prevMonth ? `Pick a session after the one month ${ctx.prevMonth} was set at.` : 'Pick a later session.'
    case 'session_not_yet': return 'Pick a session that has started, or starts in the next 12 hours.'
    default: break
  }
  // a write the database doesn't have yet (before migration 80 is on it): PostgREST's PGRST202, or a 404 on the rpc
  if (code === 'PGRST202' || err?.status === 404 || /Could not find the function/i.test(msg)) return "That isn't on the database yet, so nothing was changed."
  if (code === '42501') return 'Only an owner or admin can do that.'
  // a win the database refused: a blank title, 1 over its limit (LIMITS.winTitle) or a category outside the 5
  if (code === '23514') return PANEL.winTitleBad
  return null
}

// ---- Today: the term jobs (hub.today_jobs kinds term_final_month and term_past_end) -------------------

export const TERM_CHIP = 'Term' // undrawn
export function termJobDetail(job) {
  const d = job.detail || {}
  const months = d.term_months || 12
  const head = job.kind === 'term_past_end'
    ? `Month ${d.term_month || months + 1}, past the ${months}-month term.`
    : `Final month of ${months}.`
  return `${head} Access follows the subscription, so nothing changes on its own.`
}

// ---- Groups on Recordings and dates (owner, admin) ----------------------------------------------------

export const GROUPS = {
  addGroup: name => `Add ${name}`,
  addEvery: 'Add a session for every group',
  every: 'Every group',
  everyPattern: 'Sessions for every group',
  everyTitle: 'Session for every group',
  groupsOf: product => `${product}'s groups`,
  added: name => `Added. ${name} has no sessions yet, so add its first.`,
  addedEvery: 'Added. Set its date and Zoom link.',
  rename: 'Rename',
  cancel: 'Cancel',
  renameLabel: 'Group name',
  renameSave: 'Save the name',
  renamed: name => `Saved. The group is called ${name} now.`,
  nameBad: "A group's name can be 1 to 80 characters.",
  nameTaken: name => `There's already a group called ${name}.`,
  remove: 'Remove the group',
  removeConfirm: name => `Remove ${name}? Its WhatsApp link goes with it. Nobody is in it and it has no sessions.`,
  removeYes: 'Remove it',
  removeNo: 'Keep it',
  removed: name => `${name} is removed.`,
  removeRefused: "Only a group with nobody in it, no sessions and no prices can be removed. Move its members to another group on their pages first.",
  cantRemove: "A group with sessions or recordings stays, so its members' history keeps its place.",
}

// The group a new one is called: the product's naming ("Group {n}"), after the highest number used
export function nextGroup(product, cohorts = []) {
  const mine = cohorts.filter(c => c.product_id === product.id)
  const used = mine.map(c => c.number ?? 0)
  const fromNames = mine.map(c => Number((/(\d+)\s*$/.exec(c.name || '') || [])[1] || 0))
  const n = Math.max(0, ...used, ...fromNames, mine.length) + 1
  const pattern = product.cohort_naming?.pattern || product.settings?.cohort_naming?.pattern || 'Group {n}'
  const name = pattern.replace('{n}', String(n))
  return { number: n, name }
}
// "Add group 3" (the name's first letter in lower case, as Add cohort 4)
export const addGroupLabel = name => GROUPS.addGroup(name ? name[0].toLowerCase() + name.slice(1) : name)

// ---- Registers ----------------------------------------------------------------------------------------

export const REGISTER = {
  // a group's register says what it counts towards (undrawn)
  groupLede: 'Mark who came. Mark an absence agreed in advance as excused.',
  markAll: 'Mark all present',
  markAllHint: 'Anyone already marked keeps their mark.',
  markAllDone: n => `${plural(n, 'person', 'people')} marked present.`,
  nobodyLeft: 'Everyone is marked.',
}

// ---- Recording tags (Recordings and dates' Tags box) ----------------------------------------------------

export const TAGS = {
  tooMany: 'A recording can have up to 40 tags.', // undrawn (22023 too_many_tags)
  tooLong: 'A tag can be up to 60 characters.', // undrawn (22023 tag_too_long)
}
// The database's own rule (private.tidy_recording_tags): trimmed, inner spaces collapsed, blanks and
// repeats (ignoring case) dropped, then at most 40 tags of at most 60 characters. The words, or null.
export function tagsProblem(text) {
  const seen = new Set()
  const tags = []
  for (const raw of String(text || '').split(',')) {
    const t = raw.trim().replace(/\s+/g, ' ')
    if (!t || seen.has(t.toLowerCase())) continue
    seen.add(t.toLowerCase())
    tags.push(t)
  }
  if (tags.length > 40) return TAGS.tooMany
  if (tags.some(t => t.length > 60)) return TAGS.tooLong
  return null
}
